import Box from '@zenbusiness/zui/Box';
import Grid from '@zenbusiness/zui/Grid';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import ConfirmDeleteInstantFundingDebitCardModal from '#client/components/ConfirmDeleteInstantFundingDebitCardModal';
import InstantDepositModal from '#client/components/MoveMoney/InstantDepositModal';
import { TRY_ADD_CARD_AGAIN_STEP } from '#client/components/MoveMoney/InstantDepositModal/constants/instants';
import { ASTRA_STATUS } from '#client/constants/astra';
import { REFRESH_AFTER_INSTANT_DEPOSIT } from '#client/constants/eventName';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useBankingPath from '#client/hooks/useBankingPath';
import useEventListeners from '#client/hooks/useEventListeners';
import useInstantBankingCard from '#client/hooks/useInstantBankingCard';
import useInstantDepositLimit from '#client/hooks/useInstantDepositLimit';
import useModalContext from '#client/hooks/useModalContext';
import { formatAccountNumber } from '#client/utils/text';

import {
  ButtonStyled,
  CaptionDescription,
  CaptionTitle,
  InfoContainer
} from './DebitCardInfo.styles';

export default function DebitCardInfo({
  uuid,
  cardCompany,
  cardHolderFirstName,
  cardHolderLastName,
  cardExpirationDate,
  isCardValidForTransfers,
  cardLastFourDigits,
  isCardExpired
}) {
  const { openModal } = useModalContext();
  const navigate = useNavigate();
  const bankingPath = useBankingPath();
  const {
    loading: instantDepositLimitLoading,
    transfers: instantDepositTransfers,
    refetchInstantDepositLimit
  } = useInstantDepositLimit();
  const { isDailyTransferLimitExceeded } = instantDepositTransfers;

  // we need to listen for this to fetch updated isDailyTransferLimitExceeded after an instant deposit transfer.
  useEventListeners([
    { eventName: REFRESH_AFTER_INSTANT_DEPOSIT, eventHandler: refetchInstantDepositLimit }
  ]);

  const { createBankingCardForInstantFunding, loading: createCardLoading } =
    useInstantBankingCard();
  const {
    loading: loadingAstraProfile,
    isNeedCreateDestinationCard,
    refreshAstraProfile,
    isEligibleForTransfers,
    accountStatus
  } = useAstraProfileContext();

  const cardHolderName = [cardHolderFirstName, cardHolderLastName].join(' ');
  const formatCardNumber = formatAccountNumber({
    accountNumber: cardLastFourDigits ?? '',
    formatCharacter: '•',
    numberFormatCharacter: 2
  });
  const title = [cardCompany, formatCardNumber].join(' ');

  const handleClickDepositButton = async () => {
    if (isNeedCreateDestinationCard) {
      const { error: createCardError } = await createBankingCardForInstantFunding();
      if (createCardError) {
        openModal({
          component: InstantDepositModal,
          initStep: TRY_ADD_CARD_AGAIN_STEP,
          instantFundingAccount: {
            uuid
          }
        });

        return;
      }
      refreshAstraProfile();
    }

    openModal({
      component: InstantDepositModal,
      instantFundingAccount: {
        uuid
      }
    });
  };

  const handleSuccess = () => {
    const dashboardPath = `${bankingPath}/dashboard`;
    refreshAstraProfile();
    navigate(dashboardPath, { replace: true });
  };

  const handleDeleteCard = () => {
    openModal({
      component: ConfirmDeleteInstantFundingDebitCardModal,
      id: uuid,
      handleSuccess
    });
  };

  const disableMoveMoneyButton =
    !isCardValidForTransfers ||
    createCardLoading ||
    loadingAstraProfile ||
    instantDepositLimitLoading ||
    isDailyTransferLimitExceeded ||
    !!isCardExpired ||
    accountStatus === ASTRA_STATUS.SUSPENDED ||
    !isEligibleForTransfers;

  return (
    <Box mb="32px">
      <Stack
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexDirection={{ xs: 'column', md: 'row' }}
        gap="16px"
        justifyContent="space-between"
        mb="32px"
      >
        <Stack>
          {!cardCompany ? (
            <Skeleton width="200px" />
          ) : (
            <Heading
              size="3xl"
              weight="bold"
            >
              {title}
            </Heading>
          )}
        </Stack>
        <ButtonStyled
          color="primary"
          disabled={disableMoveMoneyButton}
          disableRipple
          onClick={handleClickDepositButton}
          variant="contained"
        >
          Deposit
        </ButtonStyled>
      </Stack>
      <InfoContainer
        direction="column"
        gap={{ xs: '32px', md: '48px' }}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <Heading
            size="2xl"
            weight="bold"
          >
            Info
          </Heading>
          <LoadingButton
            color="info"
            disabled={createCardLoading || loadingAstraProfile}
            disableRipple
            onClick={handleDeleteCard}
            variant="text"
          >
            <Text
              color="red"
              size="md"
              sx={{ opacity: createCardLoading || loadingAstraProfile ? 0.5 : 1 }}
              weight="medium"
            >
              Delete
            </Text>
          </LoadingButton>
        </Stack>
        <Grid
          columnSpacing={{ xs: 0, md: '32px' }}
          container
          rowSpacing={{ xs: '32px', md: 0 }}
        >
          <Grid
            item
            md={4}
            xs={12}
          >
            <Stack spacing={{ xs: '4px', md: '20px' }}>
              <CaptionTitle
                color="tertiary"
                size="sm"
              >
                CARDHOLDER NAME
              </CaptionTitle>
              <CaptionDescription
                color="primary"
                size="xs"
              >
                {!cardHolderFirstName || !cardHolderLastName ? <Skeleton /> : cardHolderName}
              </CaptionDescription>
            </Stack>
          </Grid>
          <Grid
            item
            md={4}
            xs={12}
          >
            <Stack spacing={{ xs: '4px', md: '20px' }}>
              <CaptionTitle
                color="tertiary"
                size="sm"
              >
                EXPIRATION DATE
              </CaptionTitle>
              <CaptionDescription
                color="primary"
                size="xs"
              >
                {!cardExpirationDate ? <Skeleton /> : cardExpirationDate}
              </CaptionDescription>
            </Stack>
          </Grid>
        </Grid>
      </InfoContainer>
    </Box>
  );
}

DebitCardInfo.propTypes = {
  uuid: PropTypes.string,
  cardCompany: PropTypes.string,
  cardHolderFirstName: PropTypes.string,
  cardHolderLastName: PropTypes.string,
  cardExpirationDate: PropTypes.string,
  isCardValidForTransfers: PropTypes.bool,
  cardLastFourDigits: PropTypes.string,
  isCardExpired: PropTypes.bool
};
